import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, FlexControlledForm, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { NON_ASSIGNABLE_VALUE, OrganizationTypes } from '@/common/constants';
import FormLoader from '@/modules/employer/components/SetupEmployer/FormLoader';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { useGetInternalSubAccountNumberQuery } from '@/modules/employer/components/SetupEmployer/queries/useGetInternalSubAccountNumber.query';
import { useSaveTypeStore } from '@/modules/employer/components/SetupEmployer/stores';
import { useBankMethodsQuery } from '@/modules/employer/hooks';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';

import useBanksList from '../BanksList/useBanksList';

import useAdminContributionsFields from './hooks/useAdminContributionsFields';
import useAdministratorFeesFields from './hooks/useAdministratorFeesFields';
import useBalanceAdjustmentFields from './hooks/useBalanceAdjustmentFields';
import useBankAccountForRefundsFields from './hooks/useBankAccountForRefundsFields';
import useFundingReplenishmentFields from './hooks/useFundingReplenishmentFields';
import useIndividualContributionFields from './hooks/useIndividualContributionFields';
import usePayrollContributionFields from './hooks/usePayrollContributionFields';
import usePreFundFields from './hooks/usePreFundFields';
import { AccountUsageValues } from './AccountUsage.types';
import { useAccountUsageStore } from './stores';

const StyledBox = styled(Box)`
  input {
    max-width: 180px;
  }
`;

interface Props {
  onDirty?: (isDirty: boolean) => void;
  disabled?: boolean;
}

const subtitle = (text: string) => (
  <Text size="14px" margin={{ top: 'spacing8', bottom: '18px' }}>
    {text}
  </Text>
);

const AccountUsage: React.FC<Props> = ({ onDirty, disabled }) => {

  const { formattedData, isLoading } = useGetEmployer();
  const { viewMode } = useSetupEmployerMode();
  const {
    handleSetValues,
    state: {
      holdPeriod,
      contributionPushGiveUpDays,
      fundingReplenishmentBank,
      payrollContributionBank
    },
  } = useAccountUsageStore();
  const currentOrgType = useMemo(() => formattedData?.type, [formattedData]);
  const isAdminFeesIsAvailable = useMemo(
    () => currentOrgType === OrganizationTypes.system
    || currentOrgType === OrganizationTypes.partner,
    [currentOrgType]);
  const { methods, isLoading: areBankMethodsLoading } = useBankMethodsQuery();
  const { list: banks, isLoading: areBanksLoading } = useBanksList();
  const [currentValues, setCurrentValues] = useState<AccountUsageValues>({
    ...(isAdminFeesIsAvailable ? {} : { administratorFeesBank: NON_ASSIGNABLE_VALUE }),
  });

  const [adminFeesValues, setAdminFeesValues] = useState({});
  const [payrollContributionValues, setPayrollContributionValues] = useState({});
  const [individualContributionValues, setIndividualContributionValues] = useState({});
  const [fundingReplenishmentValues, setFundingReplenishmentValues] = useState({});
  const [adminContributions, setAdminContributions] = useState({});
  const [preFundValues, setPreFundValues] = useState({});
  const [balanceAdjustmentValues, setBalanceAdjustmentValues] = useState({});
  const [bankAccountForRefundsValues, setBankAccountForRefundsValues] = useState({});
  const [additionalLoading, setAdditionalLoading] = useState(false);
  const { activeStatus, pendingStatus } = useSaveTypeStore();
  const isDataLoading = useMemo(() => isLoading
    || areBankMethodsLoading
    || areBanksLoading,
  [isLoading, areBankMethodsLoading, areBanksLoading]);

  const pushContributionAccountId = useMemo(() => formattedData?.normalProperties?.contributionClearingAccount, [formattedData]);
  const pushReplenishmentAccountId = useMemo(() => formattedData?.normalProperties?.notionalClearingAccount, [formattedData]);
  const {
    accountNumber: hsaClearingAccountNumber,
    routingNumber: hsaClearingRoutingNumber,
  } = useGetInternalSubAccountNumberQuery(pushContributionAccountId);
  const {
    accountNumber: notionalClearingAccountNumber,
    routingNumber: notionalClearingRoutingNumber,
  } = useGetInternalSubAccountNumberQuery(pushReplenishmentAccountId);

  const administratorFeesFields = useAdministratorFeesFields(
    banks,
    formattedData,
    currentValues,
    !pendingStatus,
    isDataLoading,
  );
  const payrollContributionFields = usePayrollContributionFields(
    banks,
    methods,
    formattedData,
    currentValues,
    !pendingStatus,
    isDataLoading,
    hsaClearingAccountNumber,
    hsaClearingRoutingNumber,
  );
  const fundingReplenishmentFields = useFundingReplenishmentFields(
    banks,
    methods,
    formattedData,
    currentValues,
    !pendingStatus,
    isDataLoading,
    notionalClearingAccountNumber,
    notionalClearingRoutingNumber,
  );
  const adminContributionsFields = useAdminContributionsFields({
    formattedData,
    isLoading: isDataLoading,
  });

  const individualContributionFields = useIndividualContributionFields(
    formattedData,
    !pendingStatus,
    isDataLoading,
  );

  const preFundFields = usePreFundFields(
    formattedData,
    !pendingStatus,
    isDataLoading,
    currentValues.allowPrefund,
  );

  const balanceAdjustmentFields = useBalanceAdjustmentFields(
    banks,
    methods,
    formattedData,
    currentValues,
    !pendingStatus,
    isDataLoading,
    hsaClearingAccountNumber,
    hsaClearingRoutingNumber,
  );

  const bankAccountForRefundsFields = useBankAccountForRefundsFields(
    banks,
    formattedData,
    !pendingStatus,
    isDataLoading,
  );

  useEffect(() => {
    setCurrentValues((prev) => ({
      ...prev,
      ...adminFeesValues,
      ...payrollContributionValues,
      ...individualContributionValues,
      ...adminContributions,
      ...fundingReplenishmentValues,
      ...preFundValues,
      ...balanceAdjustmentValues,
      ...bankAccountForRefundsValues,
    }));
  }, [
    adminFeesValues,
    payrollContributionValues,
    adminContributions,
    fundingReplenishmentValues,
    preFundValues,
    individualContributionValues,
    balanceAdjustmentValues,
    bankAccountForRefundsValues,
  ]);

  useEffect(() => {
    if (!areBankMethodsLoading && !areBanksLoading) {
      handleSetValues(currentValues);
    }

  }, [
    handleSetValues,
    areBankMethodsLoading,
    areBanksLoading,
    currentValues,
  ]);

  useEffect(
    () => {
      if (formattedData.overridableProperties?.payrollContributionsHoldPeriod
        && !holdPeriod) {
        handleSetValues({
          holdPeriod: formattedData.overridableProperties?.payrollContributionsHoldPeriod,
        });
      }
      if (formattedData.overridableProperties?.contributionPushGiveUpDays
        && !contributionPushGiveUpDays) {
        handleSetValues({
          contributionPushGiveUpDays: formattedData.overridableProperties?.contributionPushGiveUpDays,
        });
      }
    },
    [
      formattedData.overridableProperties,
      holdPeriod,
      handleSetValues,
      contributionPushGiveUpDays,
    ],
  );

  useEffect(() => {
    if (formattedData?.normalProperties?.fundingReplenishmentBank
      && !fundingReplenishmentBank) {
      handleSetValues({
        fundingReplenishmentBank: formattedData?.normalProperties?.fundingReplenishmentBank,
      });
    }
    if (formattedData?.normalProperties?.payrollContributionBank
      && !payrollContributionBank) {
      handleSetValues({
        payrollContributionBank: formattedData?.normalProperties?.payrollContributionBank,
      });
    }
  }, [
    formattedData?.normalProperties,
    fundingReplenishmentBank,
    payrollContributionBank,
    handleSetValues,
  ]);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    let timer: NodeJS.Timeout;
    if (!isDataLoading) {
      setAdditionalLoading(true);
      timer = setTimeout(() => {
        setAdditionalLoading(false);
      }, 100);
    }
  
    return () => {
      clearTimeout(timer);
    };
  }, [isDataLoading]);

  if (isDataLoading || additionalLoading) return <FormLoader testId="AccountUsage" />;
  
  return (
    <Box>
      {(isAdminFeesIsAvailable && !!administratorFeesFields.length) && (
        <Box id="administrator_fees">
          <FlexControlledForm
            formTitle="Partner Administrator Fees"
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            fields={administratorFeesFields}
            onChangeValues={setAdminFeesValues}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none', paddingBottom: 0 }}
            formStyle={{ marginBottom: 0 }}
            isThinMode
          />
        </Box>
      )}
      {!!payrollContributionFields.length && (
        <Box id="payroll_contributions">
          <FlexControlledForm
            formTitle="Payroll Contribution"
            subTitle={subtitle('Elevate collects contributions made to employee-owned accounts, such as an HSA, prior to posting to the employee\'s account')}
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            fields={payrollContributionFields}
            onChangeValues={setPayrollContributionValues}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none', paddingBottom: 0 }}
            formStyle={{ marginBottom: 0 }}
            isThinMode
          />
        </Box>
      )}
      {!!individualContributionFields.length && (
        <Box id="individual_contributions">
          <FlexControlledForm
            formTitle="Individual Contribution"
            // subTitle={subtitle('The number of days to be hold during individual contribution')}
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            fields={individualContributionFields}
            onChangeValues={setIndividualContributionValues}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none', paddingBottom: 0 }}
            formStyle={{ marginBottom: 0, marginTop: '18px' }}
            isThinMode
          />
        </Box>
      )} 
      {!!adminContributionsFields.length && (
        <Box id="admin_contributions">
          <FlexControlledForm
            formTitle="Admin Contributions"
            subTitle={subtitle('Create a method for employers to setup contributions to apply to employees accounts')}
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            fields={adminContributionsFields}
            onChangeValues={setAdminContributions}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none' }}
            formStyle={{ marginTop: '18px', marginBottom: 0 }}
            isThinMode
          />
        </Box>
      )} 

  
      {!!fundingReplenishmentFields.length && (
        <Box id="funding_replenishment">
          <FlexControlledForm
            formTitle="Funding Replenishment"
            subTitle={subtitle('Funds collected to replenish the plans account after claim payment have been made')}
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            fields={fundingReplenishmentFields}
            onChangeValues={setFundingReplenishmentValues}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none', paddingBottom: currentValues?.fundingReplenishmentBank ? 0 : undefined }}
            formStyle={{ marginTop: '18px', marginBottom: currentValues?.fundingReplenishmentBank ? 0 : undefined }}
            isThinMode
          />
        </Box>
      )}
     
      {!!preFundFields.length && (
        <StyledBox id="pre_fund">
          <FlexControlledForm
            formTitle="Pre-Fund"
            subTitle={subtitle('Elevate holds a portion of member enrollments to support quick claim payments. Each month elections will be re-calculated and determine if additional funds will need to be collected')}
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            fields={preFundFields}
            onChangeValues={setPreFundValues}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none' }}
            formStyle={{ marginTop: '18px' }}
            isThinMode
          />
        </StyledBox>
      )}

      {!!balanceAdjustmentFields.length && (
        <Box id="balance_adjustment">
          <FlexControlledForm
            formTitle="Balance Adjustment"
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            fields={balanceAdjustmentFields}
            onChangeValues={setBalanceAdjustmentValues}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none' }}
            formStyle={{ marginTop: '18px' }}
            isThinMode
          />
        </Box>
      )}

      {!!bankAccountForRefundsFields.length && (
        <Box id="bank_account_for_refunds">
          <FlexControlledForm
            formTitle="Bank Account for Refunds for Push Refunds"
            editMode={!viewMode && !formattedData?.isTerminated && !disabled}
            subTitle={subtitle('Used solely to return credits or refunds. This account will not be debited.')}
            fields={bankAccountForRefundsFields}
            onChangeValues={setBankAccountForRefundsValues}
            showError={activeStatus || pendingStatus}
            onDirty={onDirty}
            wrapperStyle={{ border: 'none' }}
            formStyle={{ marginTop: '18px' }}
            isThinMode
          />
        </Box>
      )}
      
    </Box>
  );
};

export default AccountUsage;
